import { getAccessToken, updateAccessToken, clearTokens, isTokenExpired, getTokenExpiresAt } from '../authTokens';
import { authApi } from './auth';
import type { TenantConfig } from '@shared/types/tenant-config';
import type { components } from '@/types/api.generated';
import type { UploadedFile } from '@/types/files';
import { getSubdomain } from '@/utils/environment';
import { classifyError, ErrorType } from '@/utils/errorClassifier';
import { useNetworkStatus } from '@/stores/networkStatusStore';

// Re-export generated types for convenience
export type { components };

// Re-export UploadedFile for convenience
export type { UploadedFile };

/**
 * Response from /api/tenant/public endpoint
 * Note: This is intentionally PARTIAL (nested structure) for security
 * Only exposes public-relevant fields (terminology, branding, icons, parent portal status)
 */
export interface PublicTenantConfigResponse {
  tenant: {
    id: string;
    subdomain: string;
    schoolName: string;
    timezone: string;
    settings: {
      terminology: TenantConfig['terminology'];
      branding: TenantConfig['branding'];
      modules?: {
        parentPortal?: {
          enabled: boolean;
          allowBehaviorView: boolean;
          allowAttendanceView: boolean;
          parentAccountsAutoApprove: boolean;
        } | null;
      };
    };
    allowedModules: {
      parentPortal: boolean; // Only parent portal permission exposed publicly
    };
  };
}

// In-memory token cache to avoid localStorage hangs on backgrounded tabs
let cachedAccessToken: string | null = null;
let tokenExpiresAt: number | null = null;

// Token refresh mutex to prevent concurrent refresh attempts
let tokenRefreshPromise: Promise<string | null> | null = null;

/**
 * Set the cached access token (called by auth store on login/refresh)
 */
export function setCachedToken(accessToken: string | null, expiresAt?: number) {
  cachedAccessToken = accessToken;
  tokenExpiresAt = expiresAt || null;
}

/**
 * Clear the cached access token (called on logout)
 */
export function clearCachedToken() {
  cachedAccessToken = null;
  tokenExpiresAt = null;
}

/**
 * Timeout wrapper for promises to prevent hanging
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number = 5000): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Operation timed out after ${timeoutMs}ms`)), timeoutMs)
    ),
  ]);
}

/**
 * Retry a function with exponential backoff
 * Only retries for errors classified as retryable (network errors, service down)
 */
async function withRetry<T>(fn: () => Promise<T>, maxRetries: number = 3, baseDelayMs: number = 1000): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      const classified = classifyError(err);

      // Don't retry non-retryable errors (auth failures, etc)
      if (!classified.shouldRetry) {
        throw err;
      }

      // Last attempt - throw the error
      if (attempt === maxRetries - 1) {
        throw err;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelayMs * Math.pow(2, attempt);
      console.warn(`[Auth] Refresh attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Get authorization headers with current JWT token
 * Automatically refreshes expired tokens
 * Uses a mutex to prevent concurrent refresh attempts
 */
const getAuthHeaders = async (): Promise<HeadersInit> => {
  let accessToken: string | null = null;

  // Try to use cached token first (faster)
  if (cachedAccessToken && tokenExpiresAt) {
    const now = Date.now();

    // If cached token is still valid (with 10s buffer), use it
    if (tokenExpiresAt > now + 10000) {
      accessToken = cachedAccessToken;
    } else {
      clearCachedToken();
    }
  }

  // If no cached token, try to get from localStorage
  if (!accessToken) {
    accessToken = getAccessToken();

    // Update cache if we got a token
    if (accessToken) {
      const expiresAt = getTokenExpiresAt();
      if (expiresAt) {
        setCachedToken(accessToken, expiresAt);
      }
    }
  }

  // If no valid token available or token is expired, try to refresh
  if (!accessToken || isTokenExpired(10000)) {
    console.warn('[Auth] Token expired, attempting refresh...');

    // Use mutex to prevent concurrent refresh attempts
    if (!tokenRefreshPromise) {
      // Retry with exponential backoff for network errors
      // No refreshToken parameter - cookie is sent automatically
      tokenRefreshPromise = withRetry(
        () => withTimeout(authApi.refresh(), 5000),
        3, // max 3 attempts
        1000 // 1s base delay (1s, 2s, 4s backoff)
      )
        .then((result) => {
          console.log('[Auth] Token refresh successful');
          // Update storage and cache with new token
          updateAccessToken(result.accessToken, result.expiresIn);
          setCachedToken(result.accessToken, Date.now() + result.expiresIn * 1000);
          return result.accessToken;
        })
        .catch((err) => {
          const classified = classifyError(err);
          console.error('[Auth] Token refresh failed after retries:', err);

          // Only clear tokens for auth failures (not network errors)
          // For network errors, keep tokens - user may come back online
          if (classified.shouldLogout || classified.type === ErrorType.INVALID_SESSION) {
            clearTokens();
            clearCachedToken();
          }
          return null;
        })
        .finally(() => {
          tokenRefreshPromise = null;
        });
    }

    // Wait for refresh to complete (whether we started it or it was already running)
    accessToken = await tokenRefreshPromise;
  }

  // Build headers with common fields
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if we have one
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Add tenant subdomain header for multi-tenant routing
  const subdomain = getSubdomain();
  if (subdomain) {
    headers['X-Tenant-Subdomain'] = subdomain;
  }

  return headers;
};

/**
 * Make an authenticated API request
 * Automatically handles 401 errors by logging out the user
 */
export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(endpoint, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      // Parse error response (with fallback)
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));

      // Create error object with status, code, and validation details
      const error = Object.assign(new Error(errorData.error || errorData.message || `HTTP ${response.status}`), {
        status: response.status,
        code: errorData.code,
        details: errorData.details, // Preserve validation error details
      });

      // Classify the error
      const classified = classifyError(error);

      // Update network status if it's a network-related error
      if (classified.type === ErrorType.AUTH_SERVICE_DOWN) {
        useNetworkStatus.getState().markOffline();
        console.warn('[Network] Authentication service unavailable');
      }

      // Clear cached token for auth errors
      if (response.status === 401) {
        clearCachedToken();
      }

      // Throw classified error (auth handler will decide if logout is needed)
      throw Object.assign(error, {
        type: classified.type,
        shouldRetry: classified.shouldRetry,
        shouldLogout: classified.shouldLogout,
      });
    }

    // If we successfully got a response, mark as online
    useNetworkStatus.getState().markOnline();

    return response.json();
  } catch (error: unknown) {
    // If error is already classified (from response handling above), just throw it
    if (error && typeof error === 'object' && 'type' in error) {
      console.error('[API] Request failed:', endpoint, error);
      throw error;
    }

    // Network error (fetch failed - no response)
    const classified = classifyError(error);

    if (classified.type === ErrorType.NETWORK_ERROR) {
      useNetworkStatus.getState().markOffline();
      console.error('[Network] Connection failed:', endpoint, error);
    }

    // Log API errors for debugging
    console.error('[API] Request failed:', endpoint, error);

    // Throw classified error
    throw Object.assign(error as object, {
      type: classified.type,
      shouldRetry: classified.shouldRetry,
      shouldLogout: classified.shouldLogout,
    });
  }
}

/**
 * Generic API client (axios-style interface)
 * Used by some components for flexible API calls
 */
export const api = {
  get: async <T = unknown>(url: string, config?: { responseType?: 'json' | 'blob' }) => {
    if (config?.responseType === 'blob') {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api${url}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return { data: await response.blob() };
    }
    return { data: await apiRequest<T>(`/api${url}`, { method: 'GET' }) };
  },

  post: async <T = unknown>(url: string, data?: unknown, config?: { responseType?: 'json' | 'blob' }) => {
    if (config?.responseType === 'blob') {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api${url}`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return { data: await response.blob() };
    }
    return {
      data: await apiRequest<T>(`/api${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    };
  },
};

/**
 * Get access token for components that need direct access
 * (e.g., file uploads that can't use apiRequest)
 */
export async function getAuthToken(): Promise<string | null> {
  // Try cached token first
  if (cachedAccessToken && tokenExpiresAt && tokenExpiresAt > Date.now() + 10000) {
    return cachedAccessToken;
  }

  // Try localStorage
  const token = getAccessToken();
  if (token) {
    return token;
  }

  // Try to refresh (no refreshToken parameter - cookie is sent automatically)
  try {
    const result = await authApi.refresh();
    updateAccessToken(result.accessToken, result.expiresIn);
    setCachedToken(result.accessToken, Date.now() + result.expiresIn * 1000);
    return result.accessToken;
  } catch {
    clearTokens();
    clearCachedToken();
  }

  return null;
}
